import {isInteger, isUndefined} from 'lodash';

import {globalWindow} from '../../../../libs/shared/helpers/src/browser/browserHelpers';
import {isElectron} from '../../../../libs/shared/helpers/src/environment/platformHelpers';
import {AsyncHandler} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {ExtendedError} from '../../../../libs/shared/helpers/src/types/customErrors';
import {deleteRawBootCacheAsync} from '../../../../srcCommon/services/boot/rawBootCache';
import {reportToBugsnag} from '../../../../srcCommon/services/bugsnag/bugsnag';
import {deleteOfflineResourcesAsync} from '../../../../srcCommon/services/offline/offlineResources';
import {keyValueStore} from '../../../../srcCommon/services/storage/storage';
import {CellsConversationsDragItem} from '../../../helpers/conversationCellHelpers';
import {ApiTeammateDataGroup} from '../../../models/api/dataGroupModel';
import {
  AdvancedNotificationRule,
  ApiTeammateNotificationRules,
  TeammateNotificationRules,
} from '../../../models/api/notificationRulesModel';
import {ApiTeammate, TeammateModel} from '../../../models/api/teammateModel';
import {userSignedOut} from '../../../services/electron/electronBridgeOutbound';
import {deleteApiAsync, postAsync, putApiAsync} from '../../../services/http/http';
import {HttpError} from '../../../services/http/httpError';
import {findTeammateQueue} from '../../../services/tasks/specializedTaskQueues';
import {ThunkAction, ThunkAsyncAction} from '../../appThunk';
import {canMoveToTagAction, moveToTagAction} from '../conversations/tagConversationsActions';
import {updateDataAction} from '../dataGroupsActions';
import {getUser, getUserId, getUserNotificationRules} from './teammatesSelectors';

/*
 * User actions.
 */

export function saveUserAction(
  newUser: Pick<Partial<TeammateModel>, 'isAvailable' | 'givenName' | 'familyName' | 'alias' | 'phone'>,
): ThunkAsyncAction<TeammateModel | null> {
  return async (dispatch, getState) => {
    // Find the queue for our user.
    const queue = findTeammateQueue(dispatch, getState, getState().data.userId);
    const user = queue.get();
    if (!user) {
      throw new Error('Our user should be in the state.');
    }

    // Update the user immediately.
    const updatedUser = {...user, ...newUser};
    queue.update({teammates: [updatedUser]});

    await queue.waitForTurnAsync();

    try {
      // Perform the request.
      // Avoid serializing the entire teammate to guarantee updates are limited to what was explicitely included in the newUser.
      const body = {
        available: newUser.isAvailable,
        given_name: newUser.givenName,
        family_name: newUser.familyName,
        alias: newUser.alias,
        phone: newUser.phone,
      };
      const serverDataGroup = await putApiAsync(ApiTeammateDataGroup, updatedUser.url, body);

      return queue.provideResultAsync(serverDataGroup);
    } catch (error) {
      queue.reportError();
      throw error;
    }
  };
}

export function updateUserAvatar(
  avatarFile: File,
  onProgress?: (progress: number) => void,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const user = getUser(getState());
    const userAvatarUrl = `${user.url}/avatar`;

    const formData = new FormData();
    formData.append('avatar', avatarFile);

    try {
      const updatedUser = await putApiAsync(ApiTeammate, userAvatarUrl, formData, {
        onProgressChanged: (progress) => {
          const progressPercent = Math.min(Math.round((100 * progress.loaded) / progress.total), 100);
          onProgress?.(progressPercent);
        },
      });
      dispatch(updateDataAction({teammates: [updatedUser]}));
    } catch (error) {
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }
      throw error;
    }
  };
}

export function deleteUserAvatar(): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const user = getUser(getState());
    const userAvatarUrl = `${user.url}/avatar`;

    try {
      const updatedUser = await deleteApiAsync(ApiTeammate, userAvatarUrl);
      dispatch(updateDataAction({teammates: [updatedUser]}));
    } catch (error) {
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }
      throw error;
    }
  };
}

export function updateAdvancedNotificationRule(
  notificationRule: AdvancedNotificationRule,
  notificationIndex?: number,
): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const currentNotificationRules = getUserNotificationRules(getState());
    const newNotificationRules = [...currentNotificationRules.advanced];

    if (
      !isUndefined(notificationIndex) &&
      isInteger(notificationIndex) &&
      newNotificationRules[notificationIndex]
    ) {
      newNotificationRules[notificationIndex] = notificationRule;
    } else {
      newNotificationRules.push(notificationRule);
    }

    dispatch(
      updateUserNotificationRules({
        ...currentNotificationRules,
        advanced: newNotificationRules,
      }),
    );
  };
}

export function deleteAdvancedNotificationRule(notificationIndex: number): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const currentNotificationRules = getUserNotificationRules(getState());
    const newNotificationRules = [...currentNotificationRules.advanced];
    newNotificationRules.splice(notificationIndex, 1);

    dispatch(
      updateUserNotificationRules({
        ...currentNotificationRules,
        advanced: newNotificationRules,
      }),
    );
  };
}

export function updateUserNotificationRules(notificationRules: TeammateNotificationRules): ThunkAsyncAction {
  return async (dispatch, getState) => {
    const user = getUser(getState());
    const userNotifRulesUrl = `${user.url}/notif_rules`;

    try {
      const updatedNotifRules = await putApiAsync(
        ApiTeammateNotificationRules,
        userNotifRulesUrl,
        ApiTeammateNotificationRules.serialize(notificationRules),
      );
      dispatch(
        updateDataAction({
          teammates: [{...user, notificationRules: updatedNotifRules}],
        }),
      );
    } catch (error) {
      if (!(error instanceof HttpError)) {
        reportToBugsnag(error);
      }
      throw error;
    }
  };
}

export function signoutUserAsyncAction(clearPersistedStateAsync: AsyncHandler): ThunkAsyncAction {
  return async () => {
    // Clear the session.
    await postAsync('/api/1/signout');

    // Clear all storage.
    const clearStorageAsync = async () => keyValueStore.clear();
    await Promise.all(
      [
        clearStorageAsync(),
        deleteOfflineResourcesAsync(),
        clearPersistedStateAsync(),
        deleteRawBootCacheAsync(),
      ].map(catchClearStorageErrorAsync),
    );

    if (isElectron()) {
      userSignedOut();
    }

    // Redirect to the login page.
    globalWindow.location.href = '/';
  };
}

export function signoutOtherSessionsActionAsync(): ThunkAsyncAction {
  return async () => {
    // Clear the session.
    await postAsync('/api/1/signout_others');
  };
}

async function catchClearStorageErrorAsync(promise: Promise<unknown>): Promise<void> {
  try {
    await promise;
  } catch (error) {
    reportToBugsnag(new ExtendedError('Failed to clear storage.', error));
  }
}

/*
 * Other push actions.
 */

export function pushTeammateUpdated(teammate: TeammateModel): ThunkAction {
  return async (dispatch, getState) => {
    const userId = getUserId(getState());
    if (teammate.id !== userId) {
      // If the teammate is not the current user, we can update synchronously.
      dispatch(updateDataAction({teammates: [teammate]}));
      return;
    }

    // Otherwise, we need to lock via the queue.
    const queue = findTeammateQueue(dispatch, getState, teammate.id);

    // Wait for our turn to work, then update the teammate.
    await queue.waitForTurnAsync();

    // Update the state.
    queue.provideResultAsync({
      teammates: [teammate],
      updatedAt: teammate.updatedAt,
    });
  };
}

/*
 * Drag & Drop actions.
 */

export function canDropOnTeammateInboxTagAction(
  options: CellsConversationsDragItem,
  teammate: TeammateModel,
) {
  return canMoveToTagAction(options, teammate.inboxTagId, teammate.namespace);
}

export function moveToTeammateInboxTagAction(
  options: CellsConversationsDragItem,
  teammate: TeammateModel,
  preventUndo?: boolean,
) {
  return moveToTagAction(options, teammate.inboxTagId, preventUndo);
}
